/**
 * Named as an "inc" file just to state this is not to be used anywhere else,
 * and only be included in the main index file
 */

import { HttpClient } from '@sentry/integrations'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { mapKeys } from 'lodash-es'
import history from 'router/history'
import { store } from 'state/_store'
import { getAccount } from 'state/Account'
import { currentVersion, Locale, platform } from 'utils'

//no need to capture any development error and send to Sentry ¯\_(ツ)_/¯
const IS_PROD = platform.isProd()

//TODO move this to some API call or the likes, so we can change it without a redeploy
const ALWAYS_CAPTURE_ACCOUNTS = [
	'chefsasiantn',
	'greatwalldoylestown',
	'eatneastbranchburg',
	'tokyogrillsc'
]

//TODO use an ErrorBoundary so at least we can continue after the error... hopefully? https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
Sentry.init({
	dsn: 'https://4f3bae433a8449d8b3752a0d7eedebce@o4504561268424704.ingest.sentry.io/4504630628777984',

	//https://docs.sentry.io/platforms/javascript/guides/react/configuration/releases/
	release    : `manager-react@${currentVersion()}`,
	environment: platform.env(),

	replaysSessionSampleRate: IS_PROD? 0.1 : 0, //so we can sniff on how users tend to use the app :))
	replaysOnErrorSampleRate: IS_PROD? 1.0 : 0, //so we can see how the heck they managed to break our pretty app :((

	//if tree-shaking is desired for integrations: https://docs.sentry.io/platforms/javascript/guides/react/configuration/tree-shaking/#tree-shaking-default-integrations
	integrations: [
		//Redux integration DOES NOT happen here; it's configured with the sentryReduxEnhancer() at _store.js!
		//however, we DO change the Redux state sent to events at the bottom of this file, at beforeSend()

		new BrowserTracing({
			routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
		}),

		//this is NOT necessary to include console messages in error reports - it creates a new issue/entry/message for every single thing logged to the console!
		// new CaptureConsole(),

		//captures errored HTTP requests; cannot capture headers/cookies with sendDefaultPii=false
		new HttpClient({
			failedRequestStatusCodes: [402, [404, 499], [500, 599]],
			// failedRequestTargets: [/\/manager\/api\//]
		}),

		new Sentry.Replay(),

		//might be useful if we need to append data to errors
		//https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/plugin/#extraerrordata
		// new ExtraErrorData(),

		//Changes the file paths send to Sentry
		//https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/plugin/#rewriteframes
		// new RewriteFrames({
		// 	root: '',
		// 	prefix: '',
		// })
	],

	//set to true to capture personally identifiable information; BEWARE OF THE GDPR RISKS!!
	//with this set to false, the HttpClient cannot capture headers and cookies
	sendDefaultPii: false,

	//the default is 3, but then we move up to 10, so we can properly read the Redux state tree
	normalizeDepth: 10,

	//https://docs.sentry.io/platforms/javascript/configuration/options/#initial-scope
	initialScope: {
		tags: {
			logger: 'react', //this field exists by default in PHP reports, but not in React reports... so we're "normalizing" that - see https://github.com/getsentry/sentry/issues/46574
		},

		contexts: {
			culture: {
				locale  : Locale.currentLang(),
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
			app: {
				app_version      : window.ENV?.VERSION,
				app_identifier   : window.ENV?.FLAVOR, //this was expected to be user.segment, but it doesn't work?
				build_type       : window.ENV?.VERSION? 'native' : 'web',
				'Cordova Version': window.cordova?.version,
				'SDK Version'    : window.device?.sdkVersion,
			},
			device: {
				manufacturer        : window.device?.manufacturer,
				model               : window.device?.model,
				simulator           : !!window.device?.isVirtual,
				'Is disguised iPad?': platform.isReallyAnIPad()? 'YES!' : 'no',
				'Unique identifier' : window.device?.uuid,
			},
		},

		//other user details are set at App.js, since we need to check if they're already logged in
		user: { ip_address: '{{auto}}' }
	},

	//More "weird" errors might be filtered out by using this whole list https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
	ignoreErrors: [
		'unstable' //TODO replace by error codes after sc-37508
	],
	denyUrls    : [
		// Chrome extensions
		/extensions\//i,
		/^chrome:\/\//i,
	],

	//https://docs.sentry.io/platforms/javascript/guides/react/configuration/sampling/
	sampleRate: IS_PROD, //100% of errors will be sent to Sentry - but performance tracing is governed by the function below
	tracesSampler: samplingContext => {
		const id_string = getAccount(store.getState()).id_string
		if (ALWAYS_CAPTURE_ACCOUNTS.includes(id_string)) {
			return true
		}

		return IS_PROD? 0.2 : 0
	},

	//here we (1) cleanup auth tokens and (2) compact the damn state so it fits the Sentry requirements of ~16kb, moving it from the usual context (max: 8kb) to additional data (16kb)
	beforeSend: e => {
		const state = e.contexts.state.state.value

		//TODO This got mostly removed because, after much struggle compressing it all, I realized it was possible to simply move it into the "additional data" section, which allows ~16kb per entry 🤡🤡🤡 That said, we might still get truncated, given 40 orders could go up to ~34kb, and 150 items up to 19kb. As of this writing, that 16kb limit per-key didn't seem to be be in effect.
		// See https://develop.sentry.dev/sdk/expected-features/data-handling/#variable-size

		// eslint-disable-next-line @miovision/disallow-date/no-new-date
		// const toEpoch = dateStr => new Date(dateStr).getTime() / 1000
		// const toEpochIfDateStr = v => /^\d{4}-\d\d-\d\dT\d\d:\d\d/.test(v)? toEpoch(v) : v
		// const miniKey = (key, length = 4) => key == 'display_name'? 'name' : key.substring(0, length)
		// const miniKeys = obj => obj|>mapKeys(#, (_, key) => {
		// 	const mini = miniKey(key)
		// 	return ['newO', 'ship'].includes(mini)? miniKey(key, 9) : mini //settings.newOrder* / account.shipday_*
		// })

		//Space-saving mechanics:
		// Sentry caps data blobs to around 18kb. Thus, we're preemptively cleaning it beforehand, so "most" common stuff gets sent, instead of losing half of the state because of alpha-sort trimming. Average data sizes from big lists:
		// - orders.list: 852b, based on 40 orders from my test sampledelivery
		// - invoices.list: 158b, based on 8 invoices from my test sampledelivery
		// - items.list: 128b, based on 2019's vitospizza with 147 items (includes changes to the categories list as well)
		// - "empty" state after logging in: ~7.7kb
		// const newState = {
			// ...state,
		// 	//first, we remove mostly-useless fields from the error dump
		// 	account: {
		// 		...state.account,
		// 		current: miniKeys(omit(state.account.current, ['canned_messages', 'logo', 'location_wording_plural', 'location_wording_singular', 'display_zero_dollar_items', 'app_ids'])),
		// 	},
		// 	restaurants: {
		// 		...state.restaurants,
		// 		list: state.restaurants.list.map(rest => [
		// 			rest.id,
		// 			rest
		// 				|>mapValues(#, toEpochIfDateStr)
		// 				|>omit(#, ['id', 'color', 'closed_until_texts', 'show_future_order_delivery_minutes', 'show_future_order_pickup_minutes', 'position'])
		// 				|>miniKeys
		// 		])
		// 			|>Object.fromEntries(#)
		// 	},
		//
		// 	//from some other state pieces, we simply compact their keys
		// 	auth: {
				// ...state.auth,
				// token: '…',
		// 		user: miniKeys({
		// 			...state.auth.user,
		// 			tablet_options: miniKeys(state.auth.user.tablet_options),
		// 		})
		// 	},
		// 	categories: {
		// 		...state.categories,
		// 		list: state.categories.list
		// 			.map(cat => [
		// 				cat.id,
		// 				miniKeys(omit(cat, ['id', 'description', 'tag'])),
		// 			])
		// 			|>Object.fromEntries(#)
		// 	},
		// 	constants: Object.keys(state.constants).map(constant => miniKey(constant)),
		// 	_persist: Object.values(state._persist),
		// 	push    : Object.values(state.push),
		// 	request : miniKeys(state.request),
		// 	reseller: state.reseller? miniKeys(omit(state.reseller, ['colors', 'logo', 'email', 'support_phone'])) : state.reseller,
		// 	settings: {
		// 		...state.settings,
		// 		settings: miniKeys(state.settings.settings)
		// 	},
		//
		// 	//then, we start "compressing" lists that can get quite long, so we still know overall what's in there
		// 	invoices: {
		// 		...state.invoices,
		// 		//"compress" all invoices. This saves ~105b per invoice on a list of 8 invoices = 0.8kb of savings
		// 		list: state.invoices.list
		// 			.map(i => [i.id, [i.invoice_link_guid, toEpoch(i.created_at), i.sent, i.total_amount_due]])
		// 			|>Object.fromEntries(#)
		// 	},
		//
		// 	items: {
		// 		...state.items,
		// 		// icons: state.items.icons.map(icon => [icon.id, icon.name]),
		// 		icons: state.items.icons.map(icon => [icon.id, icon.name])|>Object.fromEntries(#),
		// 		list: state.items.list
		// 			//"compress" after the 5th. This saves ~69b per item on a list of 159 items = 11kb of savings
		// 			.map((item, index) =>  (index < 5)? item : [item.id, item.display_name, item.category_id])
		// 			//group by the category_id (only after compacting, as to maintain only 5 complete items in overall)
		// 			|>groupBy(#, entry => Array.isArray(entry)? entry[2] : entry.category_id)
		// 			|>mapValues(#, group => group
		// 			.map(item => Array.isArray(item)? dropRight(item, 1) : [item.id, item]) //...then remove it from the compact items
		// 			|>Object.fromEntries(#))
		// 	},
		//
		// 	orders: {
		// 		...state.orders,
		// 		list: state.orders.list
		// 			|>groupBy(#, 'restaurant_id')
		// 			|>mapValues(#, group => group
		// 			.map((order, index) => {
		// 				//"compact" after the 3rd. This saves ~600b per order on a list of 40 orders = 24kb of savings
		// 				return [order.id,
		// 				        (index < 3)?
		// 					        omit(order, 'id')|>miniKeys
		// 				        :
		// 					        [
		// 						        toEpoch(order.ready_at),
		// 						        order.order_type.substring(0,2),
		// 						        +order.is_cancelled,
		// 						        +order.is_asap,
		// 						        order.shipday_order?.id? `s${order.shipday_order.id}` : null
		// 					        ]|>dropRightWhile(#, field => field === null)
		// 				]
		// 			})
		// 			|>Object.fromEntries(#))
		// 	},
		// }

		//last, we hope no one ever hit an error with ALL those lists full to the brink 🙏
		//An example state (40 orders, 8 invoices, 159 items, 16 categories, 4 restaurants) went from 63.7kb to 14.8kb
		// const originalSize   = JSON.stringify(state).length+' kb'
		// const compressedSize = JSON.stringify(e.contexts.state.state.value).length+' kb'
		// console.log(
		// 	`Results of state compression before sending to Sentry: ${originalSize} => ${compressedSize}`,
		// 	// e.contexts.state.state.value
		// )

		e.contexts.state = {
			// ...e.contexts.state,
			// original: originalSize,
			// compressed: compressedSize,
			NOT_HERE: '-= MOVED TO ADDITIONAL DATA =-'
		}
		e.extra = e.extra || {} //sometimes this is not present (Sentry doesn't try to leave it as an empty object...)
		Object.assign(e.extra, mapKeys(state, (_, key) => key == 'auth'? 'state.au{dontErase}th' : `state.${key}`))

		return e
	},
})
